import {
  ArrowBigLeft,
    ArrowRightFromLine,
    Download,
    Edit2,
    Eye,
    RefreshCw,
    Search,
    Trash2,
    X,
  } from "lucide-react";
  import React, { useEffect, useState } from "react";
  import { useNavigate } from "react-router-dom";
  import * as XLSX from "xlsx";
  import api from "../services/api";
  
  const AdditionalParticipantsList = ({ user, API_BASE_URL, getAuthHeader, logout }) => {
    const [addParticipants, setAddParticipants] = useState([]);
    const [filteredAddParticipants, setFilteredAddParticipants] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
  
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
  
    const navigate = useNavigate();
  
    // Check screen width
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [isTablet, setIsTablet] = useState(window.innerWidth < 1024);
  
    useEffect(() => {
      const handleResize = () => {
        setIsMobile(window.innerWidth < 768);
        setIsTablet(window.innerWidth < 1024);
      };
  
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);
  
    const fetchAddParticipants = async () => {
      setRefreshing(true);
      try {
        const data = await api.registration.getAllAddRegistrations();
        setAddParticipants(data);
        setFilteredAddParticipants(data);
      } catch (error) {
        console.error("Error fetching additional participants:", error);
        showNotification(error.message || "Failed to load additional participants", "error");
      } finally {
        setRefreshing(false);
        setLoading(false);
      }
    };
  
    useEffect(() => {
      fetchAddParticipants();
    }, []);
  
    // Apply search filter
    useEffect(() => {
      if (!searchTerm.trim()) {
        setFilteredAddParticipants(addParticipants);
      } else {
        const lowerSearch = searchTerm.toLowerCase();
        const filtered = addParticipants.filter((p) =>
          Object.values({
            mobile: p.mobile,
            orderId: p.orderId,
            transactionId: p.transactionId,
            paymentStatus: p.paymentStatus,
            paymentMethod: p.paymentMethod,
          })
            .join(" ")
            .toLowerCase()
            .includes(lowerSearch)
        );
        setFilteredAddParticipants(filtered);
      }
      setCurrentPage(1); // Reset to first page when search changes
    }, [searchTerm, addParticipants]);
  
    const completedPayments = addParticipants.filter(
      (p) => p.paymentStatus === "Success" || p.paymentStatus === "Paid"
    ).length;
  
    const showNotification = (message, type = "info") => {
      const existingNotification = document.querySelector(".custom-notification");
      if (existingNotification) {
        existingNotification.remove();
      }
  
      const notification = document.createElement("div");
      notification.className = `custom-notification alert alert-${
        type === "error" ? "danger" : type === "success" ? "success" : "info"
      }`;
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 9999;
        min-width: 300px;
        max-width: 500px;
        padding: 15px;
        border-radius: 5px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        animation: slideIn 0.3s ease-out;
      `;
  
      notification.innerHTML = `
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <strong>${
              type === "error"
                ? "❌ Error"
                : type === "success"
                ? "✅ Success"
                : "ℹ️ Info"
            }</strong>
            <div class="mt-1">${message}</div>
          </div>
          <button class="btn btn-sm btn-link text-dark" onclick="this.parentElement.parentElement.remove()">
            ×
          </button>
        </div>
      `;
  
      document.body.appendChild(notification);
  
      setTimeout(() => {
        if (notification.parentElement) {
          notification.remove();
        }
      }, 5000);
    };
  
    const downloadExcel = () => {
      const ws = XLSX.utils.json_to_sheet(
        paginatedAddParticipants.map((p) => ({
          ID: p.id,
          Mobile: p.mobile,
          "Spouse": p.accompany?.spouse || 0,
          "Guest": p.accompany?.guest || 0,
          "Driver": p.accompany?.driver || 0,
          "Child Above 5": p.accompany?.childAbove5 || 0,
          "Child Below 5": p.accompany?.childBelow5 || 0,
          "Total Amount": p.totalAmount,
          "Registration Date": p.registrationDate,
          "Order ID": p.orderId,
          "Transaction ID": p.transactionId,
          "Bank Transaction ID": p.bankTransactionId,
          "Payment Status": p.paymentStatus,
          "Payment Gateway Status": p.paymentGatewayStatus,
          "Payment Method": p.paymentMethod,
        }))
      );
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Additional Participants");
      XLSX.writeFile(
        wb,
        `additional_participants_${new Date().toISOString().split("T")[0]}.xlsx`
      );
    };
  
    const handleView = (id) => {
      navigate(`/additional-registration-details/${id}`);
    };
  
    const handleBack = () => {
      navigate(-1);
    };

    const handleDelete = async (id) => {
      if (!window.confirm("Are you sure you want to delete this additional payment record?"))
        return;
  
      try {
        const headers = getAuthHeader();
        headers["Content-Type"] = "application/json";
  
        const response = await fetch(`${API_BASE_URL}/api/add-registrations/${id}`, {
          method: "DELETE",
          headers: headers,
        });
  
        if (response.status === 401) {
          logout();
          navigate("/login");
          return;
        }
  
        const result = await response.json();
        if (!response.ok) throw new Error(result.message || "Failed to delete");
  
        setAddParticipants(addParticipants.filter((p) => p.id !== id));
        showNotification("Additional payment record deleted successfully", "success");
      } catch (error) {
        console.error("Delete failed:", error);
        showNotification(
          "Failed to delete record: " + error.message,
          "error"
        );
      }
    };
  
    // Pagination logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const paginatedAddParticipants = filteredAddParticipants.slice(
      indexOfFirstItem,
      indexOfLastItem
    );
    const totalPages = Math.ceil(filteredAddParticipants.length / itemsPerPage);
  
    const handlePageChange = (pageNumber) => {
      setCurrentPage(pageNumber);
      window.scrollTo({ top: 0, behavior: "smooth" });
    };
  
    const handleItemsPerPageChange = (e) => {
      setItemsPerPage(parseInt(e.target.value));
      setCurrentPage(1);
    };
  
    const getTableHeaders = () => {
      if (isMobile) {
        return [
          { key: "participant", label: "Payment Info", className: "col-6" },
        //   { key: "actions", label: "Actions", className: "col-6" },
        ];
      } else if (isTablet) {
        return [
          { key: "mobile", label: "Mobile", className: "col-3" },
          { key: "payment", label: "Payment", className: "col-3" },
          { key: "amount", label: "Amount", className: "col-2" },
        //   { key: "actions", label: "Actions", className: "col-4" },
        ];
      }
      return [
        { key: "mobile", label: "Mobile", className: "" },
        { key: "accompany", label: "Accompanying", className: "" },
        { key: "payment", label: "Payment Status", className: "" },
        { key: "transaction", label: "Transaction Info", className: "" },
        { key: "method", label: "Payment Method", className: "" },
        { key: "amount", label: "Amount", className: "" },
        { key: "date", label: "Date", className: "" },
        // { key: "actions", label: "Actions", className: "" },
      ];
    };
  
    const MobileRow = ({ participant }) => (
      <div className="card mb-3">
        <div className="card-body">
          <div className="row">
            <div className="col-8">
              <h6 className="mb-1">{participant.mobile}</h6>
              <div className="small">
                <div>Order: {participant.orderId || "N/A"}</div>
                <div>Txn: {participant.transactionId || "N/A"}</div>
                <div className="mt-2">
                  <span
                    className={`badge ${
                      participant.paymentStatus === "Success" ||
                      participant.paymentStatus === "Paid"
                        ? "bg-success"
                        : participant.paymentStatus === "Cancelled" ||
                          participant.paymentStatus === "Failed" ||
                          participant.paymentStatus === "Refunded"
                        ? "bg-danger"
                        : "bg-warning"
                    }`}
                  >
                    {participant.paymentStatus}
                  </span>
                </div>
              </div>
            </div>
            <div className="col-4">
              <div className="d-flex flex-column gap-2">
                <div className="fw-bold">
                  ৳{participant.totalAmount?.toLocaleString()}
                </div>
                <div className="d-flex flex-column gap-1">
                  {(user?.role === "ADMIN" || user?.role === "EDITOR") && (
                    <button
                      onClick={() => handleDelete(participant.id)}
                      className="btn btn-sm btn-outline-danger d-flex align-items-center justify-content-center"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                  {/* <button
                    onClick={() => handleView(participant.id)}
                    className="btn btn-sm btn-outline-primary d-flex align-items-center justify-content-center"
                    title="View"
                  >
                    <Eye size={14} />
                  </button> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  
    const TabletRow = ({ participant }) => (
      <tr>
        <td>
          <div>
            <strong>{participant.mobile}</strong>
          </div>
        </td>
        <td>
          <span
            className={`badge ${
              participant.paymentStatus === "Success" ||
              participant.paymentStatus === "Paid"
                ? "bg-success"
                : participant.paymentStatus === "Cancelled" ||
                  participant.paymentStatus === "Failed" ||
                  participant.paymentStatus === "Refunded"
                ? "bg-danger"
                : "bg-warning"
            }`}
          >
            {participant.paymentStatus}
          </span>
          <div className="small mt-1">{participant.paymentMethod || "N/A"}</div>
        </td>
        <td className="fw-bold">৳{participant.totalAmount?.toLocaleString()}</td>
        <td>
          <div className="d-flex gap-1 flex-wrap">
            {/* <button
              onClick={() => handleView(participant.id)}
              className="btn btn-sm btn-outline-primary"
              title="View"
            >
              <Eye size={14} />
            </button> */}
            {(user?.role === "ADMIN" || user?.role === "EDITOR") && (
              <button
                onClick={() => handleDelete(participant.id)}
                className="btn btn-sm btn-outline-danger"
                title="Delete"
              >
                <Trash2 size={14} />
              </button>
            )}
            <div className="small mt-2">
              {(() => {
                const date = new Date(participant.registrationDate);
                const day = String(date.getDate()).padStart(2, "0");
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const year = date.getFullYear();
                return `${day}/${month}/${year}`;
              })()}
            </div>
          </div>
        </td>
      </tr>
    );
  
    if (loading && addParticipants.length === 0) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading additional participants...</p>
        </div>
      );
    }
  
    return (
      <div className="card shadow-sm">
        <div className="card-body">
          {/* Header with Actions */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
            <h2 className="mb-0">Additional Payments List</h2>
            <div className="d-flex flex-wrap gap-2">
            <button
                className="btn btn-warning d-flex align-items-center gap-2"
                onClick={handleBack}
              >
                <ArrowBigLeft size={18} />
                <span className="d-none d-sm-inline">Back</span>
              </button>
              <div className="d-flex align-items-center">
                <span className="badge bg-primary me-2 py-3">
                  Total Amount: ৳
                  {addParticipants
                    .reduce((sum, p) => sum + (p.totalAmount || 0), 0)
                    .toLocaleString()}
                </span>
                <span className="badge bg-success me-2 py-3">
                  Paid: {completedPayments}
                </span>
                <span className="badge bg-info py-3">
                  Total: {addParticipants.length}
                </span>
              </div>
              <button
                className="btn btn-success d-flex align-items-center gap-2"
                onClick={downloadExcel}
              >
                <Download size={18} />
                <span className="d-none d-sm-inline">Export</span>
              </button>
              <button
                className="btn btn-outline-primary d-flex align-items-center gap-2"
                onClick={fetchAddParticipants}
                disabled={refreshing}
              >
                <RefreshCw size={18} className={refreshing ? "spin" : ""} />
                <span>{refreshing ? "Refreshing..." : "Refresh"}</span>
              </button>
            </div>
          </div>
  
          {/* Search */}
          <div className="mb-3">
            <div className="row">
              <div className="col-md-6">
                <div className="input-group">
                  <span className="input-group-text">
                    <Search size={16} />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by mobile, order ID, transaction ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => setSearchTerm("")}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
  
          {/* Pagination Controls - Top */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex align-items-center gap-3">
              <div className="d-flex align-items-center gap-2">
                <span className="small text-muted">Show:</span>
                <select
                  className="form-select form-select-sm"
                  style={{ width: "auto" }}
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>
              <div className="small text-muted">
                Showing {indexOfFirstItem + 1} to{" "}
                {Math.min(indexOfLastItem, filteredAddParticipants.length)} of{" "}
                {filteredAddParticipants.length} records
                {searchTerm && ` matching "${searchTerm}"`}
              </div>
            </div>
          </div>
  
          {/* Content */}
          {paginatedAddParticipants.length > 0 ? (
            <>
              {/* Mobile View */}
              {isMobile ? (
                <div>
                  {paginatedAddParticipants.map((participant) => (
                    <MobileRow
                      key={participant.id}
                      participant={participant}
                    />
                  ))}
                </div>
              ) : /* Tablet View */
              isTablet ? (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="table-light">
                      <tr>
                        {getTableHeaders().map((header) => (
                          <th key={header.key} className={header.className}>
                            {header.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedAddParticipants.map((participant) => (
                        <TabletRow
                          key={participant.id}
                          participant={participant}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                /* Desktop View */
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="table-light">
                      <tr>
                        {getTableHeaders().map((header) => (
                          <th key={header.key}>{header.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedAddParticipants.map((p) => (
                        <tr key={p.id}>
                          <td>
                            <div className="fw-bold">{p.mobile}</div>
                          </td>
                          <td>
                            <div className="small">
                              <div>Spouse: {p.accompany?.spouse || 0}</div>
                              <div>Guest: {p.accompany?.guest || 0}</div>
                              <div>Driver: {p.accompany?.driver || 0}</div>
                              <div>Child Above 5: {p.accompany?.childAbove5 || 0}</div>
                              <div>Child Below 5: {p.accompany?.childBelow5 || 0}</div>
                            </div>
                          </td>
                          <td>
                            <span
                              className={`badge ${
                                p.paymentStatus === "Success" ||
                                p.paymentStatus === "Paid"
                                  ? "bg-success"
                                  : p.paymentStatus === "Cancelled" ||
                                    p.paymentStatus === "Failed" ||
                                    p.paymentStatus === "Refunded"
                                  ? "bg-danger"
                                  : "bg-warning"
                              }`}
                            >
                              {p.paymentStatus}
                            </span>
                          </td>
                          <td>
                            <div className="small">
                              <div>
                                <strong>Order:</strong> {p.orderId || "N/A"}
                              </div>
                              <div>
                                <strong>Txn:</strong> {p.transactionId || "N/A"}
                              </div>
                              <div>
                                <strong>Bank:</strong>{" "}
                                {p.bankTransactionId || "N/A"}
                              </div>
                            </div>
                          </td>
                          <td>{p.paymentMethod || "N/A"}</td>
                          <td className="fw-bold">
                            ৳{p.totalAmount?.toLocaleString()}
                          </td>
                          <td>
                            <div className="small">
                              {(() => {
                                const date = new Date(p.registrationDate);
                                const day = String(date.getDate()).padStart(2, "0");
                                const month = String(date.getMonth() + 1).padStart(2, "0");
                                const year = date.getFullYear();
                                return `${day}/${month}/${year}`;
                              })()}
                            </div>
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              {/* <button
                                onClick={() => handleView(p.id)}
                                className="btn btn-sm btn-outline-primary"
                                title="View"
                              >
                                <Eye size={16} />
                              </button> */}
                              {(user?.role === "ADMIN" || user?.role === "EDITOR") && (
                                <button
                                  onClick={() => handleDelete(p.id)}
                                  className="btn btn-sm btn-outline-danger"
                                  title="Delete"
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
  
              {/* Pagination Controls - Bottom */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                  <div className="small text-muted">
                    Page {currentPage} of {totalPages}
                  </div>
                  <nav>
                    <ul className="pagination pagination-sm mb-0">
                      <li
                        className={`page-item ${
                          currentPage === 1 ? "disabled" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                      </li>
  
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNumber;
                        if (totalPages <= 5) {
                          pageNumber = i + 1;
                        } else if (currentPage <= 3) {
                          pageNumber = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNumber = totalPages - 4 + i;
                        } else {
                          pageNumber = currentPage - 2 + i;
                        }
  
                        return (
                          <li
                            key={pageNumber}
                            className={`page-item ${
                              currentPage === pageNumber ? "active" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(pageNumber)}
                            >
                              {pageNumber}
                            </button>
                          </li>
                        );
                      })}
  
                      <li
                        className={`page-item ${
                          currentPage === totalPages ? "disabled" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-5">
              <div className="text-muted">
                <i className="bi bi-person display-6 d-block mb-2"></i>
                No additional payment records found
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };
  
  export default AdditionalParticipantsList;